import React from 'react';
import { Input } from 'reactstrap';
import {
    TermForm,
    TermFormSellerNonCompetitionRow,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { SellerLocation } from '../../../../../../slices/marketplace/loi-form/loi-form';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';

export interface TermsFormSellerCovenantsBlockProps {
    termForm: TermForm;
    sellerLocations: SellerLocation[];
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    rowComments?: (string | null)[];
    onRowCommentChange?: (rowIndex: number, comment: string | null) => void;
    isAdmin?: boolean;
    adminRowComments?: (string | null)[];
    onAdminRowCommentChange?: (rowIndex: number, adminComment: string | null) => void;
}

type SellerCovenantNumberField =
    | 'practiceSoldTermMonths'
    | 'practiceSoldDistanceMiles'
    | 'otherCorporatePracticeTermMonths'
    | 'otherCorporatePracticeDistanceMiles';

const SELLER_COVENANT_TOOLTIPS: Record<SellerCovenantNumberField, string> = {
    practiceSoldTermMonths:
        'How many months after closing the seller is restricted from competing near the practice(s) sold.',
    practiceSoldDistanceMiles:
        'The radius, in miles from the practice(s) sold, within which the seller is restricted from competing.',
    otherCorporatePracticeTermMonths:
        'How many months after closing the seller is restricted from competing near other corporate-owned practices.',
    otherCorporatePracticeDistanceMiles:
        'The radius, in miles from other corporate-owned practices, within which the seller is restricted from competing.',
};

function emptySellerCovenantRow(locationId: string, locationName: string): TermFormSellerNonCompetitionRow {
    return {
        locationId,
        locationName,
        practiceSoldTermMonths: null,
        practiceSoldDistanceMiles: null,
        otherCorporatePracticeTermMonths: null,
        otherCorporatePracticeDistanceMiles: null,
    };
}

interface SellerCovenantRowProps {
    row: TermFormSellerNonCompetitionRow;
    readOnly: boolean;
    showLocationColumn: boolean;
    errorPrefix: string;
    errors?: Set<string>;
    showCommentColumn: boolean;
    comment: string | null;
    isBuyerReview?: boolean;
    onFieldChange: (patch: Partial<TermFormSellerNonCompetitionRow>) => void;
    onCommentChange: (comment: string | null) => void;
    showAdminCommentColumn: boolean;
    adminComment: string | null;
    isAdminCommentEditable: boolean;
    onAdminCommentChange: (adminComment: string | null) => void;
}

const SellerCovenantRow = ({
    row,
    readOnly,
    showLocationColumn,
    errorPrefix,
    errors,
    showCommentColumn,
    comment,
    isBuyerReview,
    onFieldChange,
    onCommentChange,
    showAdminCommentColumn,
    adminComment,
    isAdminCommentEditable,
    onAdminCommentChange,
}: SellerCovenantRowProps) => {
    const renderNumberInput = (field: SellerCovenantNumberField, label: string) => (
        <Input
            type="number"
            value={row[field] ?? ''}
            disabled={readOnly}
            onChange={(e) => {
                const value = e.target.value === '' ? null : Number(e.target.value);
                onFieldChange({ [field]: value } as Partial<TermFormSellerNonCompetitionRow>);
            }}
            invalid={errors?.has(`${errorPrefix}${field}`)}
            className="form-control loi-form-input"
            aria-label={label}
        />
    );

    return (
        <tr>
            {showLocationColumn && (
                <td className="terms-form-col-item table-label-cell">{row.locationName}</td>
            )}
            <td className="terms-form-col-value">
                {renderNumberInput('practiceSoldTermMonths', 'Practice(s) Sold Term (months)')}
            </td>
            <td className="terms-form-col-value">
                {renderNumberInput('practiceSoldDistanceMiles', 'Practice(s) Sold Distance (miles)')}
            </td>
            <td className="terms-form-col-value">
                {renderNumberInput('otherCorporatePracticeTermMonths', 'Other Corporate Owned Practice(s) Term (months)')}
            </td>
            <td className="terms-form-col-value">
                {renderNumberInput('otherCorporatePracticeDistanceMiles', 'Other Corporate Owned Practice(s) Distance (miles)')}
            </td>
            {showAdminCommentColumn && (
                <td className="terms-form-col-comment terms-form-col-admin-comment">
                    <Input
                        type="textarea"
                        value={adminComment ?? ''}
                        readOnly={!isAdminCommentEditable}
                        onChange={(e) =>
                            onAdminCommentChange(e.target.value === '' ? null : e.target.value)
                        }
                        className="terms-form-comment-input"
                        aria-label="Admin comment for Non-Competition"
                    />
                </td>
            )}
            {showCommentColumn && (
                <td className="terms-form-col-comment">
                    <Input
                        type="textarea"
                        value={comment ?? ''}
                        readOnly={!isBuyerReview}
                        onChange={(e) =>
                            onCommentChange(e.target.value === '' ? null : e.target.value)
                        }
                        className="terms-form-comment-input"
                        aria-label="Comment for Non-Competition"
                    />
                </td>
            )}
        </tr>
    );
};

export const TermsFormSellerCovenantsBlock: React.FC<TermsFormSellerCovenantsBlockProps> = ({
    termForm,
    sellerLocations,
    readOnly = false,
    onChange,
    errors,
    isBuyerReview,
    isBuyerReviewConfirmed,
    rowComments,
    onRowCommentChange,
    isAdmin,
    adminRowComments,
    onAdminRowCommentChange,
}) => {
    const displayRows = sellerLocations.map(
        (loc) =>
            termForm.sellerNonCompetitionRows?.find((r) => r.locationId === loc.locationId) ??
            emptySellerCovenantRow(loc.locationId, loc.locationName)
    );

    // These fields are brand-new, so unlike every other detail table the buyer never had an earlier
    // editable pass at them — let the buyer keep filling them in during their (unconfirmed) review,
    // same carve-out TermsFormGeneralDeferredPaymentsBlock already uses for the obligor fields.
    const isBuyerEditingReview = !!isBuyerReview && !isBuyerReviewConfirmed;
    const effectiveReadOnly = isBuyerEditingReview ? false : readOnly;

    const showLocationColumn = sellerLocations.length >= 2;
    const showCommentColumn = isBuyerReview || !!isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && !readOnly;
    const showAdminCommentColumn = showCommentColumn || isAdminCommentEditable;

    const handleFieldChange = (
        locationId: string,
        locationName: string,
        patch: Partial<TermFormSellerNonCompetitionRow>
    ) => {
        const existing = termForm.sellerNonCompetitionRows ?? [];
        const updated = existing.some((r) => r.locationId === locationId)
            ? existing.map((r) => (r.locationId === locationId ? { ...r, ...patch } : r))
            : [...existing, { ...emptySellerCovenantRow(locationId, locationName), ...patch }];
        onChange?.({ ...termForm, sellerNonCompetitionRows: updated });
    };

    if (sellerLocations.length === 0) {
        return null;
    }

    return (
        <div className="terms-form-detail-block terms-form-section-underlined terms-form-seller-covenants-block">
            <div className="terms-form-block-name">Non-Competition</div>
            <div className="terms-form-price-summary-table-container">
                <table className="terms-form-price-summary-table">
                    <thead>
                        <tr>
                            {showLocationColumn && <th rowSpan={2}></th>}
                            <th colSpan={2}>Practice(s) Sold</th>
                            <th colSpan={2}>Other Corporate Owned Practice(s)</th>
                            {showAdminCommentColumn && (
                                <th className="terms-form-col-comment terms-form-col-admin-comment" rowSpan={2}>
                                    Admin Comment
                                </th>
                            )}
                            {showCommentColumn && (
                                <th className="terms-form-col-comment" rowSpan={2}>
                                    Buyer Comment
                                </th>
                            )}
                        </tr>
                        <tr>
                            <th>
                                Term (months)
                                <InfoTooltip
                                    text={SELLER_COVENANT_TOOLTIPS.practiceSoldTermMonths}
                                    idText="seller-covenant-practice-sold-term"
                                />
                            </th>
                            <th>
                                Distance (miles)
                                <InfoTooltip
                                    text={SELLER_COVENANT_TOOLTIPS.practiceSoldDistanceMiles}
                                    idText="seller-covenant-practice-sold-distance"
                                />
                            </th>
                            <th>
                                Term (months)
                                <InfoTooltip
                                    text={SELLER_COVENANT_TOOLTIPS.otherCorporatePracticeTermMonths}
                                    idText="seller-covenant-other-corporate-term"
                                />
                            </th>
                            <th>
                                Distance (miles)
                                <InfoTooltip
                                    text={SELLER_COVENANT_TOOLTIPS.otherCorporatePracticeDistanceMiles}
                                    idText="seller-covenant-other-corporate-distance"
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayRows.map((row, index) => (
                            <SellerCovenantRow
                                key={row.locationId}
                                row={row}
                                readOnly={effectiveReadOnly}
                                showLocationColumn={showLocationColumn}
                                errorPrefix={`sellerCovenant:${row.locationId}:`}
                                errors={errors}
                                showCommentColumn={showCommentColumn}
                                comment={rowComments?.[index] ?? null}
                                isBuyerReview={isBuyerReview}
                                onFieldChange={(patch) => handleFieldChange(row.locationId, row.locationName, patch)}
                                onCommentChange={(comment) => onRowCommentChange?.(index, comment)}
                                showAdminCommentColumn={showAdminCommentColumn}
                                adminComment={adminRowComments?.[index] ?? null}
                                isAdminCommentEditable={isAdminCommentEditable}
                                onAdminCommentChange={(adminComment) => onAdminRowCommentChange?.(index, adminComment)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
